/**
 * Category Icon Mapping Utility
 *
 * Maps icon_name strings from database to Lucide React components.
 * Used for rendering category icons throughout the application.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Location: apps/web/src/utils/categoryIcons.jsx
 */

import {
  Fish,
  Target as Crosshair,
  Waves,
  Mountain as _Mountain,
  Footprints,
  Bike,
  Snowflake,
  Heart,
  GraduationCap,
  Zap,
  Landmark,
  Eye as Binoculars
} from 'lucide-react';

/**
 * Icon mapping from database icon_name to Lucide React component
 */
const iconMap = {
  'fish': Fish,
  'crosshair': Crosshair,
  'water': Waves,
  'mountain': _Mountain,
  'hiking': Footprints,
  'bicycle': Bike,
  'snowflake': Snowflake,
  'heart': Heart,
  'graduation-cap': GraduationCap,
  'lightning': Zap,
  'landmark': Landmark,
  'binoculars': Binoculars
};

/**
 * Get Lucide icon component by name
 *
 * @param {string} iconName - Icon name from database (e.g., "fish", "mountain")
 * @returns {React.Component|null} Lucide icon component or null if not found
 */
export function getCategoryIcon(iconName) {
  if (!iconName) {return null;}
  return iconMap[iconName] || null;
}

/**
 * Render category icon with default fallback
 *
 * @param {string} iconName - Icon name from database
 * @param {Object} props - Props to pass to icon component (className, size, etc.)
 * @returns {React.Element} Rendered icon or fallback emoji
 */
export function CategoryIcon({ iconName, className = '', size = 24, ...props }) {
  const IconComponent = getCategoryIcon(iconName);

  if (!IconComponent) {
    return <span className={className} {...props}>🏞️</span>;
  }

  return <IconComponent className={className} size={size} {...props} />;
}

export default CategoryIcon;
